import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./commerce.css";
import "./common-view.css";
import ClientSearch from "./client-search";
import AlertModal from "../components/AlertModal";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";
import { clearAuthSession, hasValidPlatformAccess } from "../utils/auth";

const ORD_PAGE_SIZE = 5;

const ORD_PYMNT_TYPE = ["visa", "mastercard", "cash", "stripe", "paypal"];
const ORD_MERCH_STATUS = ["requested", "confirmed", "preparing", "ready"];
const ORD_DLVRY_STATUS = [
  "pickup_in_store",
  "to_be_delivered",
  "assigned",
  "accepted",
  "enroute_pickup",
  "picked_up",
  "enroute_dropoff",
  "delivered"
];
const ORD_PYMNT_STATUS = ["pending", "paid", "cancelled", "refunded", "failed"];

export default function Commerce() {
  const { t } = useTranslation();
  const auth = useAuthSession();
  const { token, clientId, isAdmin } = auth;
  const [selectedClient, setSelectedClient] = useState(null);
  const [targetClientId, setTargetClientId] = useState(clientId || "");

  const [err, setErr] = useState("");
  const [alertMsg, setAlertMsg] = useState("");

  const [disOrders, setDisOrders] = useState([]);
  const [editingDisOrders, setEditingDisOrders] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const [ordCustomerName, setOrdCustomerName] = useState("");
  const [ordCustomerPhone, setOrdCustomerPhone] = useState("");
  const [ordDeliveryAddress, setOrdDeliveryAddress] = useState("");
  const [ordSummary, setOrdSummary] = useState("");
  const [ordPaymentMethod, setOrdPaymentMethod] = useState("visa");
  const [ordPaymentStatus, setOrdPaymentStatus] = useState("pending");
  const [ordMerchantStatus, setOrdMerchantStatus] = useState("requested");
  const [ordDeliveryStatus, setOrdDeliveryStatus] = useState("assigned");
  const [ordTotal, setOrdTotal] = useState(0);
  const [ordItems, setOrdItems] = useState("{}");

  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deletingOrder, setDeletingOrder] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [disOrdedPage, setDisOrdedPage] = useState(0);
  const [pendingConfirmAction, setPendingConfirmAction] = useState(null);

  const handleClientSelect = useCallback(client => {
    setSelectedClient(client);
    setTargetClientId(client.client_id);
  }, []);

  const hasPlatformAccess = useMemo(() => {
    return hasValidPlatformAccess(selectedClient);
  }, [selectedClient]);

  useEffect(() => {
    if (!selectedClient) return;
    if (!hasPlatformAccess) {
      setAlertMsg(t("dashboard.subscriptionRequiredDesc"));
      window.location.href = "/dashboard?tab=onboarding";
    }
  }, [hasPlatformAccess, selectedClient, t]);

  useEffect(() => {
    if (!isAdmin && clientId) {
      setTargetClientId(clientId);
    }
  }, [isAdmin, clientId]);

  const isCreateMode = editingDisOrders && editingDisOrders.__mode === "create";
  const isEditMode = editingDisOrders && editingDisOrders.order_id;

  const paginatedOrders = disOrders.slice(
    disOrdedPage * ORD_PAGE_SIZE,
    (disOrdedPage + 1) * ORD_PAGE_SIZE
  );

  const hasNextOrderPage =
    (disOrdedPage + 1) * ORD_PAGE_SIZE < disOrders.length;
  const hasPrevOrderPage = disOrdedPage > 0;

  useEffect(() => {
    setDisOrdedPage(0);
  }, [disOrders]);

  function startCreateOrder() {
    if (!targetClientId) {
      setAlertMsg(t("commonLabel.selectClientFirst", "Please select a client first."));
      return;
    }
    setEditingDisOrders({ __mode: "create" });
    setOrdMerchantStatus("requested");
    setOrdItems("{}");
    setOrdCustomerName("");
    setOrdCustomerPhone("");
    setOrdPaymentMethod("visa");
    setOrdPaymentStatus("pending");
    setOrdTotal(0);
    setOrdDeliveryAddress("");
    setOrdSummary("");
    setOrdDeliveryStatus("assigned");
  }

  function cancelEditOrder() {
    setEditingDisOrders(null);
  }

  async function performSave({ mode = "save" } = {}) {
    try {
      setSavingOrder(true);
      const isAdd = mode === "add";

      const body = {
        client_id: targetClientId,
        order_id: isAdd ? null : editingDisOrders?.order_id || null,
        customer_name: ordCustomerName.trim() || "",
        customer_phone: ordCustomerPhone.trim() || "",
        delivery_address: ordDeliveryAddress.trim() || "",
        items: JSON.parse(ordItems || "{}"),
        payment_method: ordPaymentMethod,
        payment_status: ordPaymentStatus,
        total_cost: ordTotal || 0,
        merchant_status: ordMerchantStatus,
        summary: ordSummary,
        delivery_status: ordDeliveryStatus
      };

      const res = await fetch(`${BACKEND_BASE_URL}/api/processOrders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!res.ok) throw new Error("Failed to save ORDER");

      setEditingDisOrders(null);
      await loadOrderReqs(targetClientId);
    } finally {
      setSavingOrder(false);
    }
  }

  function formatOrderId(orderId) {
    if (!orderId) return "";
    return orderId.includes("-") ? orderId.split("-")[0].toUpperCase() : orderId;
  }

  async function saveOrder() {
    if (!targetClientId) return;

    if (!isEditMode) {
      await performSave();
      return;
    }

    setPendingConfirmAction("save");
    setConfirmOverwrite(true);
  }

  const loadOrderReqs = useCallback(
    async clientId => {
      const res = await fetch(`${BACKEND_BASE_URL}/api/processOrders/search`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ client_id: clientId })
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuthSession();
          throw new Error("Invalid or expired token");
        }
        throw new Error("Failed to load ORDER codes");
      }

      const data = await res.json();
      setDisOrders(Array.isArray(data) ? data : []);
    },
    [token]
  );

  useEffect(() => {
    if (!token || !targetClientId) return;
    loadOrderReqs(targetClientId).catch(e => setErr(e.message));
  }, [token, targetClientId, loadOrderReqs]);

  function getOrderDeliveryStatusLabel(status) {
    return t(`commerce.deliveryStatus.${status}`, status);
  }

  function getOrderMerchantStatusLabel(status) {
    return t(`commerce.merchantStatus.${status}`, status);
  }

  function getOrderPaymentLabel(option) {
    return t(`commerce.paymentMethod.${option}`, option);
  }

  function getOrderPaymentStatusLabel(option) {
    return t(`commerce.paymentStatus.${option}`, option);
  }

  function startEditOrder(ord) {
    setEditingDisOrders(ord);
    setOrdCustomerName(ord.customer_name || "");
    setOrdCustomerPhone(ord.customer_phone || "");
    setOrdPaymentMethod(ord.payment_method || "visa");
    setOrdPaymentStatus(ord.payment_status || "pending");
    setOrdTotal(ord.total_cost || 0);
    setOrdItems(JSON.stringify(ord.items || {}, null, 2));
    setOrdMerchantStatus(ord.merchant_status || "requested");
    setOrdDeliveryAddress(ord.delivery_address || "");
    setOrdSummary(ord.summary || "");
    setOrdDeliveryStatus(ord.delivery_status || "assigned");
  }

  async function confirmDeleteOrder() {
    if (!orderToDelete) return;
    setDeletingOrder(true);

    try {
      const res = await fetch(`${BACKEND_BASE_URL}/api/processOrders/${orderToDelete.order_id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          clearAuthSession();
          throw new Error("Invalid or expired token");
        }
        throw new Error("Delete failed");
      }

      setDisOrders(prev => prev.filter(q => q.order_id !== orderToDelete.order_id));
      setOrderToDelete(null);
    } finally {
      setDeletingOrder(false);
    }
  }

  const handleAdd = async () => {
    if (!targetClientId) {
      setAlertMsg(t("commonLabel.selectClientFirst", "Please select a client first."));
      return;
    }
    setOrdPaymentMethod("visa");
    setOrdTotal(0);
    setOrdSummary("");
    setOrdDeliveryStatus("assigned");
    await performSave({ mode: "add" });
  };

  function renderOrderForm() {
    return (
      <div className="ord-card editing" onClick={e => e.stopPropagation()}>
        <div className="ord-title">
          ✏️{" "}
          {isCreateMode
            ? t("commerce.createOrder")
            : `${t("commerce.editOrder")} (#${formatOrderId(editingDisOrders?.order_id)})`}
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="ord-label">{t("commerce.name")}</div>
          <input
            className="ord-input"
            value={ordCustomerName}
            onChange={e => setOrdCustomerName(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="ord-label">{t("commonLabel.phone")}</div>
          <input
            className="ord-input"
            value={ordCustomerPhone}
            onChange={e => setOrdCustomerPhone(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="ord-label">{t("commerce.orderItems")}</div>
          <textarea
            className="ord-textarea"
            value={ordItems}
            onChange={e => setOrdItems(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="ord-label">{t("commerce.orderPaymentMethod")}</div>
          <select
            className="ord-select"
            value={ordPaymentMethod}
            onChange={e => setOrdPaymentMethod(e.target.value)}
          >
            {ORD_PYMNT_TYPE.map(value => (
              <option key={value} value={value}>
                {t(`commerce.paymentMethod.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="ord-label">{t("commerce.totalCost")}</div>
          <input
            className="ord-input"
            type="number"
            value={ordTotal}
            onChange={e => setOrdTotal(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="ord-label">{t("commerce.orderDeliveryStatus")}</div>
          <select
            className="ord-select"
            value={ordDeliveryStatus}
            onChange={e => setOrdDeliveryStatus(e.target.value)}
          >
            {ORD_DLVRY_STATUS.map(value => (
              <option key={value} value={value}>
                {t(`commerce.deliveryStatus.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="ord-label">{t("commerce.deliveryAddress")}</div>
          <input
            className="ord-input"
            value={ordDeliveryAddress}
            onChange={e => setOrdDeliveryAddress(e.target.value)}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="ord-label">{t("commerce.orderPaymentStatus")}</div>
          <select
            className="ord-select"
            value={ordPaymentStatus}
            onChange={e => setOrdPaymentStatus(e.target.value)}
          >
            {ORD_PYMNT_STATUS.map(value => (
              <option key={value} value={value}>
                {t(`commerce.paymentStatus.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="ord-label">{t("commerce.orderMerchantStatus")}</div>
          <select
            className="ord-select"
            value={ordMerchantStatus}
            onChange={e => setOrdMerchantStatus(e.target.value)}
          >
            {ORD_MERCH_STATUS.map(value => (
              <option key={value} value={value}>
                {t(`commerce.merchantStatus.${value}`)}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="ord-label">
            {t("commerce.summary")}
            {editingDisOrders?.order_id && ` (#${formatOrderId(editingDisOrders.order_id)})`}
          </div>
          <input
            className="ord-input"
            value={ordSummary}
            onChange={e => setOrdSummary(e.target.value)}
          />
        </div>

        <div className="ord-edit-row">
          <button className="btn-mini" onClick={cancelEditOrder}>
            {t("commonLabel.cancel")}
          </button>
          <button className="btn-mini primary" disabled={savingOrder} onClick={saveOrder}>
            {t("commonLabel.save")}
          </button>
          {isCreateMode && (
            <button className="btn-mini primary" disabled={savingOrder} onClick={handleAdd}>
              {t("commonLabel.add")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="view-page">
      <div className="view-card">
        <div className="view-header">
          <div>
            <h1 className="view-title">{t("commerce.title")}</h1>
            <p className="view-subtitle">{t("commerce.subtitle")}</p>
          </div>

          <div className="view-nav">
            <select
              className="view-dropdown"
              defaultValue=""
              onChange={e => {
                const route = e.target.value;
                if (route) window.location.href = route;
              }}
            >
              <option value="" disabled>{t("dropDown.commerce-page")}</option>
              <option value="/">{t("dropDown.home-page")}</option>
              <option value="/scheduler">{t("dropDown.scheduler-page")}</option>
              <option value="/clients">{t("dropDown.clients-page")}</option>
              <option value="/crm-lite">{t("dropDown.crm-lite-page")}</option>
              <option value="/dashboard">{t("dropDown.dashboard-page")}</option>
              <option value="/qr-code">{t("dropDown.qr-page")}</option>
              <option value="/support">{t("dropDown.support-page")}</option>
              <option value="/dispatcher">{t("dropDown.dispatcher-page")}</option>
              <option value="/analytics">{t("dropDown.analytics-page")}</option>
              <option value="/tutorial">{t("dropDown.tutorial-page")}</option>
            </select>
          </div>
        </div>

        <ClientSearch onSelect={handleClientSelect} />

        <div className="ord-tools">
          <div className="ord-tools-header">
            <div className="ord-tools-title">{t("commerce.orders")}</div>
            <button
              className="btn-link ord-add-ord"
              onClick={startCreateOrder}
              disabled={savingOrder}
              title={t("commerce.addOrder")}
            >
              {t("commerce.addOrder")}
            </button>
          </div>

          {isCreateMode && <div className="commonLabel.edit">{renderOrderForm()}</div>}

          <div className="ord-list">
            {paginatedOrders.map(ord => {
              const isEditingThis = isEditMode && editingDisOrders.order_id === ord.order_id;

              return (
                <React.Fragment key={ord.order_id}>
                  {isEditingThis ? (
                    <div className="commonLabel.edit">{renderOrderForm()}</div>
                  ) : (
                    <div className="ord-row">
                      <div className="ord-item view-card" onClick={() => startEditOrder(ord)}>
                        <div className="ord-item-main">
                          <div className="ord-item-title">
                            {t("commerce.orderSummary")} (#{formatOrderId(ord.order_id)}): {ord.summary || ""}
                          </div>
                          <div className="ord-item-meta">
                            {t("commerce.name")}: {ord.customer_name || t("commonLabel.none", "—")}
                          </div>
                          <div className="ord-item-meta">
                            {t("commonLabel.phone")}: {ord.customer_phone || t("commonLabel.none", "—")}
                          </div>
                          <div className="ord-item-meta">
                            {t("commerce.orderPaymentMethod")}: {getOrderPaymentLabel(ord.payment_method) || "visa"}
                          </div>
                          <div className="ord-item-meta">
                            {t("commerce.orderPaymentStatus")}: {getOrderPaymentStatusLabel(ord.payment_status) || "pending"}
                          </div>
                          <div className="ord-item-meta">
                            {t("commerce.orderDeliveryStatus")}: {getOrderDeliveryStatusLabel(ord.delivery_status) || "assigned"}
                          </div>
                          <div className="ord-item-meta">
                            {t("commerce.orderMerchantStatus")}: {getOrderMerchantStatusLabel(ord.merchant_status) || "requested"}
                          </div>
                        </div>
                        <button
                          className="ord-delete-btn side"
                          onClick={e => {
                            e.stopPropagation();
                            setOrderToDelete(ord);
                          }}
                          title={t("commonLabel.delete")}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {disOrders.length > ORD_PAGE_SIZE && (
            <div className="common-pagination">
              {hasPrevOrderPage && (
                <button className="btn-link" onClick={() => setDisOrdedPage(p => p - 1)}>
                  {t("commonLabel.previous")}
                </button>
              )}
              {hasNextOrderPage && (
                <button className="btn-link" onClick={() => setDisOrdedPage(p => p + 1)}>
                  {t("commonLabel.next")}
                </button>
              )}
            </div>
          )}
        </div>

        {orderToDelete && (
          <div className="confirm-overlay" onClick={() => setOrderToDelete(null)}>
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              <div className="confirm-title">{t("commerce.confirmDeleteTitle")}</div>
              <div className="confirm-text">
                {t("commerce.confirmDeleteMessage")} <strong>#{formatOrderId(orderToDelete.order_id)}</strong>?
              </div>
              <div className="confirm-actions">
                <button className="btn-mini" onClick={() => setOrderToDelete(null)}>
                  {t("commonLabel.cancel")}
                </button>
                <button className="btn-mini primary" disabled={deletingOrder} onClick={confirmDeleteOrder}>
                  {t("commonLabel.delete")}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmOverwrite && (
          <div
            className="confirm-overlay"
            onClick={() => {
              setConfirmOverwrite(false);
              setPendingConfirmAction(null);
            }}
          >
            <div className="confirm-modal" onClick={e => e.stopPropagation()}>
              {(() => {
                const action = pendingConfirmAction || "save";
                const isAddConfirm = action === "add";
                return (
                  <>
                    <div className="confirm-title">
                      {t(isAddConfirm ? "commerce.createConfirmTitle" : "commerce.overwriteSaveTitle")}
                    </div>
                    <div className="confirm-text">
                      {t(isAddConfirm ? "commerce.createConfirmMessage" : "commerce.overwriteSaveMessage")}
                    </div>
                    <div className="confirm-actions">
                      <button
                        className="btn-mini"
                        onClick={() => {
                          setConfirmOverwrite(false);
                          setPendingConfirmAction(null);
                        }}
                      >
                        {t("commonLabel.cancel")}
                      </button>
                      <button
                        className="btn-mini primary"
                        onClick={async () => {
                          setConfirmOverwrite(false);
                          setPendingConfirmAction(null);
                          if (action === "add") {
                            setOrdPaymentMethod("visa");
                            setOrdSummary("");
                            await performSave({ mode: "add" });
                            return;
                          }
                          await performSave({ mode: "save" });
                        }}
                      >
                        {t(isAddConfirm ? "commerce.create" : "commerce.overwrite")}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        <AlertModal
          open={Boolean(alertMsg)}
          message={alertMsg}
          onClose={() => {
            if (!hasPlatformAccess && selectedClient) {
              window.location.href = "/dashboard?tab=onboarding";
            }
            setAlertMsg("");
          }}
        />
      </div>
    </div>
  );
}